import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { Box, Inline, Loader } from 'braid-design-system';
import throttle from 'lodash.throttle';
import { observer } from 'mobx-react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { type FC, useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import FileErrorMessage from 'src/DocumentViewer/FileErrorMessage';
import { canPreviewDocument } from 'src/DocumentViewer/utils/documentViewerAttachmentTypes';
import downloadFile from 'src/DocumentViewer/utils/downloadFile';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { TokenResult } from 'src/services/authService';
import authServiceFactory from 'src/services/authServiceFactory';
import type { IAttachment } from 'src/stores/JobApplicationStore/Attachment';
import {
  attachmentDownloadUrl,
  attachmentPreviewUrl,
} from 'src/utils/attachmentUrls';
import { ESCAPE_KEY } from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';
import { useStores } from 'src/utils/hooks/useStores';

import {
  trackDownloadDocumentEvent,
  trackExpandDocumentEvent,
  trackCollapseDocumentEvent,
} from '../../../utils/events';

import Toolbar from './Toolbar';

import * as styles from './PdfViewer.css';

const SCALE_MIN = 0.6;
const SCALE_DELTA = 0.2;
const SCALE_MAX = 2;

const pdfjsVersion = '3.11.174'; // pdfjs.version may not exist in seek-cdn
const seekCDNPrefix = `//seekcdn.com/hirer/pdf.js/${pdfjsVersion}`;
pdfjs.GlobalWorkerOptions.workerSrc = `${seekCDNPrefix}/pdf.worker.min.js`;

interface Props {
  candidateId: string;
  attachment: IAttachment;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
}

const PdfViewerWrapper: FC<Props> = ({
  candidateId,
  attachment,
  isVerifiedId,
}) => {
  const [error, setError] = useState<boolean>();
  const [numPages, setNumPages] = useState<number>(0);
  const [token, setToken] = useState<TokenResult>();
  const [scale, setScale] = useState<number>(1.2);
  const store = useStores();

  const documentOptions = useRef({
    httpHeaders: {
      Authorization: 'Bearer ABC',
    },
    standardFontDataUrl: `${seekCDNPrefix}/standard_fonts`,
    cMapUrl: `${seekCDNPrefix}/cmaps/`,
    cMapPacked: true,
  });

  const trackingAttributes = {
    candidateId,
    prospectId: attachment.jobApplication.id,
    documentType: attachment.typeName,
    attachmentId: attachment.id,
    currentPage: TRACKING_CANDIDATE_DETAILS,
  };

  const downloadDocument = () => {
    downloadFile(downloadUrl, () => trackDownload());
  };

  const collapseDocument = () => {
    store.uiStore.setDocumentExpanded(false);
    trackCollapseDocumentEvent(trackingAttributes);
  };

  const expandDocument = () => {
    store.uiStore.setDocumentExpanded(true);
    trackExpandDocumentEvent(trackingAttributes);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleZoom = useCallback(
    throttle((direction: 'zoom-in' | 'zoom-out') => {
      if (direction === 'zoom-in') {
        setScale((value) => {
          if (value < SCALE_MAX) return value + SCALE_DELTA;
          return value;
        });
      } else {
        setScale((value) => {
          if (value > SCALE_MIN) return value - SCALE_DELTA;
          return value;
        });
      }
    }, 200),
    [],
  );

  const trackDownload = async () => {
    await trackDownloadDocumentEvent({
      ...trackingAttributes,
      isVerifiedId,
    });
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === ESCAPE_KEY) {
        store.uiStore.modalStore.closeModal();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [store.uiStore.modalStore]);

  useEffect(() => {
    const fn = async () => {
      const nextToken = await authServiceFactory().getToken();
      documentOptions.current.httpHeaders.Authorization = `Bearer ${nextToken}`;
      setToken(nextToken);
    };
    fn();
  }, []);

  if (!token) {
    return null;
  }

  const downloadUrl = attachmentDownloadUrl(
    attachment.jobApplication.id,
    `${attachment.id}`,
  );

  const isExpanded = store!.uiStore.documentExpanded;

  const pdfFileUrl = attachmentPreviewUrl(
    attachment.jobApplication.id,
    `${attachment.id}`,
  );

  const onError = () => {
    setError(true);
  };

  const onLoadSuccess = ({ numPages: nextNumPages }: PDFDocumentProxy) => {
    setNumPages(nextNumPages);
  };

  const showFileErrorMessage = error || !canPreviewDocument(attachment);
  return (
    <Box
      background="surface"
      paddingX={isExpanded ? 'xlarge' : 'none'}
      width="full"
      className={{
        [styles.expandedViewer]: isExpanded,
      }}
      zIndex={2}
    >
      <Toolbar
        isExpanded={isExpanded}
        onCollapse={collapseDocument}
        onExpand={expandDocument}
        onZoomIn={() => handleZoom('zoom-in')}
        onZoomOut={() => handleZoom('zoom-out')}
        onDownload={downloadDocument}
      />
      <Box className={styles.pdfViewer} key={attachment.id}>
        {showFileErrorMessage ? (
          <FileErrorMessage
            downloadUrl={downloadUrl}
            onDownloadOriginal={() => trackDownload()}
          />
        ) : (
          <Document
            className={styles.documentViewerContainer}
            file={pdfFileUrl}
            options={documentOptions.current}
            onLoadSuccess={onLoadSuccess}
            onError={onError}
            onLoadError={onError}
            loading={
              <Box className={styles.loader}>
                <Inline space="xlarge" align="center">
                  <Loader size="large" />
                </Inline>
              </Box>
            }
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Box className={styles.pageWrap} key={`page_${index + 1}`}>
                <Page
                  className={styles.page}
                  pageNumber={index + 1}
                  loading={null}
                  scale={scale}
                  renderAnnotationLayer
                  renderTextLayer
                />
              </Box>
            ))}
          </Document>
        )}
      </Box>
    </Box>
  );
};

export default observer(PdfViewerWrapper);
